import React, { useState } from "react";

import Input from "@editor/components/common/designSystem/Input";
import useApplyComponentAction from "@editor/hooks/useApplyComponentAction";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useGetStoreNameAndUrl from "@editor/hooks/useGetStoreNameAndUrl";
import { createTemplatePreviewProps } from "@editor/hooks/useSaveComponentTemplate";
import { selectRootComponent } from "@editor/reducers/core-reducer";
import { useEditorStore } from "@editor/store";
import { trpcClient } from "@editor/utils/trpc";

import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import { useForm } from "react-hook-form";
import { z } from "zod";

const extractFontsFromUrlFormSchema = z.object({
  url: z.string().url(),
});

type ExtractFontsFromUrlFormData = z.infer<
  typeof extractFontsFromUrlFormSchema
>;

const ExtractFontsFromUrlForm = () => {
  const projectId = useCurrentProjectId();
  const { storeUrl } = useGetStoreNameAndUrl();
  const store = useEditorStore();
  const [isLoading, setIsLoading] = useState(false);
  const applyComponentAction = useApplyComponentAction();
  const rootComponent = selectRootComponent(store.getState());

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ExtractFontsFromUrlFormData>({
    resolver: zodResolver(extractFontsFromUrlFormSchema),
    defaultValues: {
      url: "",
    },
  });

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleSubmit(async ({ url }) => {
      setIsLoading(true);
      try {
        const previewRenderProps = createTemplatePreviewProps(
          store.getState(),
          storeUrl ?? "",
        );

        if (!previewRenderProps || !projectId) {
          toast({
            header: "Error",
            message: "Please save a component first",
          });
          return;
        }
        const { fonts } = await trpcClient.font.extractFontsFromUrl.query({
          url,
          projectId,
        });

        const rootFont = fonts[0];

        if (rootFont) {
          const newFontValue = rootFont ? rootFont.name : null;
          applyComponentAction({
            type: "setStyles",
            value: {
              fontFamily: newFontValue,
            },
            componentId: rootComponent?.id,
          });
        }

        toast({
          header: "Success",
          message: "Fonts have been extracted",
        });
      } catch (error) {
        toast({
          header: "Error extracting from URL",
          message: "Please try again",
        });
        console.error("Error extracting from url", error);
      } finally {
        setIsLoading(false);
      }
    })();
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-2">
      <span className="header-h5">Extract Fonts</span>
      <Input placeholder="https://www.mywebsite.com" {...register("url")} />
      {errors.url && (
        <span className="text-danger text-sm">{errors.url.message}</span>
      )}

      <Button type="submit" variant="primary" isLoading={isLoading}>
        Extract Fonts From URL
      </Button>
    </form>
  );
};

export default ExtractFontsFromUrlForm;
